/**
 * What a workflow verb means for the seat that holds it (C2).
 *
 * The label map was keyed on the ACTION alone, and a bare verb cannot
 * be honest: "approve" means *forward to the approver* for a reviewer
 * and *close this* for an approver, and the only verb on a sent-back
 * CAP is "withdraw" -- which means *pick it back up*, the opposite of
 * what the word says, as the state machine's own comment admits.
 *
 * Keyed on (action, from-state, destination), all of which the server
 * now returns, so the UI never re-derives the state machine.
 */
import type { CAPAvailableAction } from "shared/api/types";

/** The i18n key for one available action, given the CAP's state. */
export function verbKey(a: CAPAvailableAction, capState: string): string {
  if (a.action === "withdraw") {
    // From sent_back this is recovery, not retraction.
    return capState === "sent_back"
      ? "caps.detail.verbs.pickUp"
      : "caps.detail.verbs.withdraw";
  }
  if (a.action === "submit") {
    // "Resubmit" only when it HAS been round once. A first submission
    // labelled resubmit is a small lie of exactly the kind this fix
    // exists to remove -- and the drive caught it saying so on a draft
    // that had never been submitted.
    return capState === "sent_back"
      ? "caps.detail.verbs.resubmit"
      : "caps.detail.verbs.submit";
  }
  if (a.action === "approve") {
    // Where it goes is what distinguishes them.
    return a.to_state === "approved"
      ? "caps.detail.verbs.approveClose"
      : "caps.detail.verbs.approveForward";
  }
  // F -- from APPROVED this is a reopen, not a rejection. The CAP is
  // closed; nobody is waiting on it, and there is nowhere to "send it
  // back" to. Same shape as the withdraw case above: one action, two
  // meanings, and the state is what says which.
  if (capState === "approved") return "caps.detail.verbs.reopen";
  return "caps.detail.verbs.sendBack";
}

/**
 * The actions to OFFER, deduplicated.
 *
 * An admin is handed every seat's actions, which is right -- they can
 * act for any of them -- but the same verb must not appear twice.
 */
export function offeredActions(
  available: CAPAvailableAction[] | undefined,
  capState: string,
): CAPAvailableAction[] {
  const seen = new Set<string>();
  const out: CAPAvailableAction[] = [];
  for (const a of available ?? []) {
    const key = verbKey(a, capState);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(a);
  }
  return out;
}
